import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckIcon from '@mui/icons-material/Check';
import { apiFetch } from '../api/api';
import { SERVICE_ID } from '../config';
import { addOrUpdateItem, selectCartEntry } from '../store/cartSlice';
import { selectIsFavourite, toggleFavourite } from '../store/favouritesSlice';
import { mapApiProduct, isProductActive } from '../utils/mapProduct';
import {
  formatProductOriginalPrice,
  formatProductPrice,
  hasProductDiscount,
  hasProductPrice,
  isProductPriceHidden,
} from '../utils/price';
import { buildProductConsultWhatsAppMessage, openWhatsApp } from '../utils/whatsapp';
import ProductImageSlider from '../components/ProductImageSlider';
import PriceAskLabel from '../components/PriceAskLabel';
import { useCartMessage } from '../context/CartMessageContext';

function belongsToThisService(raw) {
  if (!raw?.id) return false;
  if (raw.service_id == null && raw.category?.service_id == null) return true;
  const serviceId = Number(raw.service_id ?? raw.category?.service_id);
  return serviceId === SERVICE_ID;
}

async function fetchProductById(productId) {
  const direct = await apiFetch(`/users/products/${productId}`);
  if (direct.ok) {
    const json = await direct.json();
    const raw = json.data ?? json;
    if (belongsToThisService(raw)) return mapApiProduct(raw);
  }

  let page = 1;
  let lastPage = 1;
  do {
    const response = await apiFetch(`/users/products?page=${page}`);
    if (!response.ok) break;
    const json = await response.json();
    const match = (json.data ?? []).find((p) => String(p.id) === String(productId));
    if (match && belongsToThisService(match)) return mapApiProduct(match);
    lastPage = json.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage);

  return null;
}

function DetailSectionLabel({ title }) {
  return (
    <Typography
      variant="subtitle2"
      fontWeight={800}
      sx={{
        color: 'primary.main',
        mb: 1.25,
        letterSpacing: 0.3,
        fontSize: { xs: '0.95rem', md: '1.05rem' },
      }}
    >
      {title}
    </Typography>
  );
}

const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(184,137,74,0.06)',
    borderColor: 'divider',
  },
};

export default function ProductDetail() {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showCartMessage } = useCartMessage();

  const [product, setProduct] = useState(location.state?.product ?? null);
  const [loading, setLoading] = useState(!location.state?.product);
  const [quantity, setQuantity] = useState(1);
  const [orderNote, setOrderNote] = useState('');
  const [added, setAdded] = useState(false);

  const isFavourite = useSelector(product ? selectIsFavourite(product.id) : () => false);
  const cartEntry = useSelector(product ? selectCartEntry(product.id) : () => undefined);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      const fetched = await fetchProductById(productId);
      if (!cancelled) {
        if (fetched) {
          setProduct(fetched);
        } else if (!location.state?.product) {
          setProduct(null);
        }
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [productId, i18n.language, location.state?.product]);

  useEffect(() => {
    if (cartEntry) {
      setQuantity(cartEntry.quantity);
      setOrderNote(cartEntry.note ?? '');
    } else {
      setQuantity(1);
      setOrderNote('');
    }
  }, [cartEntry, product?.id]);

  const isActive = product ? isProductActive(product) : false;
  const hidePrice = product ? isProductPriceHidden(product) : false;
  const canAddToCart = isActive && !hidePrice;
  const hasDiscount = product ? hasProductDiscount(product) : false;
  const showPrice = product ? hasProductPrice(product) : false;

  const handleAddToCart = () => {
    if (!product || !canAddToCart) return;
    dispatch(addOrUpdateItem({ product, quantity, note: orderNote.trim() }));
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
    showCartMessage(t('added_to_cart'), 'success');
  };

  const handleProductConsult = () => {
    if (!product) return;
    const message = buildProductConsultWhatsAppMessage({
      product,
      currency: t('orders_currency'),
      labels: {
        title: t('product_wa_consult_title'),
        product: t('product_wa_product'),
        price: t('product_wa_price'),
        originalPrice: t('product_wa_original_price'),
        discount: t('product_wa_discount'),
      },
    });
    openWhatsApp(message);
  };

  const primaryButtonSx = {
    py: 1.5,
    borderRadius: 2.5,
    fontSize: '1rem',
    fontWeight: 800,
    background: added ? undefined : 'linear-gradient(90deg, #D4A574, #B8894A)',
    boxShadow: added ? undefined : '0 6px 18px rgba(184,137,74,0.28)',
    '&:hover': {
      background: added ? undefined : 'linear-gradient(90deg, #B8894A, #8F6A35)',
    },
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', pb: 6 }}>
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 3, mb: 4 }}>
          <Container maxWidth="lg">
            <Skeleton variant="text" width={240} height={28} />
          </Container>
        </Box>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper {...pagePaperSx} sx={{ ...pagePaperSx.sx, p: { xs: 2, md: 3 } }}>
                <Skeleton variant="rounded" sx={{ borderRadius: 3, aspectRatio: '1 / 1' }} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper {...pagePaperSx} sx={{ ...pagePaperSx.sx, p: { xs: 2.5, md: 3.5 } }}>
                <Skeleton variant="text" height={48} width="85%" />
                <Skeleton variant="rounded" height={72} sx={{ mt: 2.5, borderRadius: 3 }} />
                <Skeleton variant="text" height={100} sx={{ mt: 3 }} />
                <Skeleton variant="rounded" height={52} sx={{ mt: 3, borderRadius: 3, width: 160 }} />
                <Skeleton variant="rounded" height={96} sx={{ mt: 3, borderRadius: 3 }} />
                <Skeleton variant="rounded" height={52} sx={{ mt: 3, borderRadius: 2.5 }} />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '60vh', py: 8 }}>
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              py: 6,
              px: 3,
              textAlign: 'center',
              borderRadius: 4,
              bgcolor: 'background.paper',
              boxShadow: '0 4px 24px rgba(184,137,74,0.06)',
            }}
          >
            <Typography variant="h5" fontWeight={800} color="primary.main" gutterBottom>
              {t('products_empty')}
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              sx={{ mt: 2, px: 4, borderRadius: 2.5, ...primaryButtonSx, py: 1.25 }}
            >
              {t('categories')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', pb: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              '& .MuiBreadcrumbs-li': { fontSize: '0.9rem' },
              '& .MuiLink-root:hover': { color: 'primary.main' },
            }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('products')}
            </Link>
            <Typography color="text.primary" fontWeight={700} noWrap sx={{ maxWidth: { xs: 180, sm: 320 } }}>
              {product.name}
            </Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              {...pagePaperSx}
              sx={{
                ...pagePaperSx.sx,
                p: { xs: 1.5, md: 2 },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {hasDiscount && product.discountPercent != null && (
                <Chip
                  label={`${product.discountPercent}% ${t('discount_off')}`}
                  color="secondary"
                  sx={{
                    position: 'absolute',
                    top: 18,
                    insetInlineStart: 18,
                    fontWeight: 800,
                    zIndex: 3,
                    boxShadow: '0 4px 12px rgba(184,137,74,0.2)',
                  }}
                />
              )}
              {!isActive && (
                <Chip
                  label={t('product_unavailable')}
                  sx={{
                    position: 'absolute',
                    top: 18,
                    insetInlineEnd: 18,
                    bgcolor: 'rgba(0,0,0,0.75)',
                    color: '#fff',
                    fontWeight: 700,
                    zIndex: 3,
                  }}
                />
              )}
              <ProductImageSlider
                images={product.images?.length ? product.images : [product.image]}
                videoUrl={product.videoUrl}
                alt={product.name}
                aspectRatio="1 / 1"
                rounded={3}
                inactive={!isActive}
              />
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              {...pagePaperSx}
              sx={{
                ...pagePaperSx.sx,
                p: { xs: 2.5, md: 3.5 },
                height: '100%',
              }}
            >
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    lineHeight={1.35}
                    sx={{
                      color: 'text.primary',
                      fontSize: { xs: '1.45rem', md: '1.85rem' },
                    }}
                  >
                    {product.name}
                  </Typography>
                  <IconButton
                    onClick={() => dispatch(toggleFavourite(product.id))}
                    aria-label={t('favourite_a11y')}
                    sx={{
                      flexShrink: 0,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: '0 2px 8px rgba(184,137,74,0.1)',
                      '&:hover': { bgcolor: alpha('#B8894A', 0.06) },
                    }}
                  >
                    {isFavourite ? <FavoriteIcon color="secondary" /> : <FavoriteBorderIcon color="primary" />}
                  </IconButton>
                </Box>

                {hidePrice ? (
                  <PriceAskLabel label={t('price_ask_message')} variant="detail" />
                ) : showPrice ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: alpha('#B8894A', 0.07),
                      border: 1,
                      borderColor: alpha('#B8894A', 0.14),
                      display: 'flex',
                      alignItems: 'baseline',
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="h4" fontWeight={800} color="primary.main" lineHeight={1.2}>
                      {formatProductPrice(product)} {t('orders_currency')}
                    </Typography>
                    {hasDiscount && (
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through', fontWeight: 600 }}
                      >
                        {formatProductOriginalPrice(product)}
                      </Typography>
                    )}
                  </Box>
                ) : null}

                {product.description && (
                  <Box>
                    <DetailSectionLabel title={t('product_description')} />
                    <Typography color="text.secondary" lineHeight={1.85} sx={{ fontSize: { xs: '0.98rem', md: '1.05rem' } }}>
                      {product.description}
                    </Typography>
                  </Box>
                )}

                <Divider />

                <Box>
                  <DetailSectionLabel title={t('quantity')} />
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      p: 0.75,
                      borderRadius: 3,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      boxShadow: 'inset 0 0 0 1px rgba(184,137,74,0.04)',
                    }}
                  >
                    <IconButton
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={!canAddToCart}
                      size="small"
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      fontWeight={800}
                      fontSize={20}
                      sx={{ minWidth: 44, textAlign: 'center', px: 0.5 }}
                    >
                      {quantity}
                    </Typography>
                    <IconButton
                      onClick={() => setQuantity((q) => q + 1)}
                      disabled={!canAddToCart}
                      size="small"
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        bgcolor: alpha('#B8894A', 0.08),
                        '&:hover': { bgcolor: alpha('#B8894A', 0.14) },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('order_note')}
                  placeholder={t('order_note_placeholder')}
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  disabled={!canAddToCart}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                    },
                  }}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 0.5 }}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={!canAddToCart}
                    onClick={handleAddToCart}
                    startIcon={added ? <CheckIcon /> : <ShoppingCartOutlinedIcon />}
                    color={added ? 'success' : 'primary'}
                    sx={primaryButtonSx}
                  >
                    {added ? t('added_to_cart') : t('add_to_cart')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleProductConsult}
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      borderColor: '#25D366',
                      color: '#128C7E',
                      '&:hover': {
                        borderColor: '#128C7E',
                        bgcolor: alpha('#25D366', 0.08),
                      },
                    }}
                  >
                    {t('product_consult_whatsapp')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/cart')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      borderColor: alpha('#B8894A', 0.35),
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha('#B8894A', 0.06),
                      },
                    }}
                  >
                    {t('view_cart')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
